import { useEffect } from "react";
import { HomePageV1 } from "./page/v1/HomePageV1";
import { BrowserRouter, Route, Routes, useLocation } from "react-router-dom";
import UsedPage from "./page/v2/used/V2_Used";
import ClubInfo from "./page/v2/used/ClubInfo";
import NotFoundScene from "./page/NotFoundScene";
import { Survey_main as SurveyMain } from "./page/v2/used/survey/Survey_main";
import { SpecialTourHomePage } from "./page/specialTour/SpecialTourHomePage";
import { RegionsPage } from "./page/specialTour/RegionsPage";
import { HotelsPage } from "./page/specialTour/HotelsPage";
import { ItinerariesPage } from "./page/specialTour/ItinerariesPage";
import { CoursesPage } from "./page/specialTour/CoursesPage";
import { DestinationsPage } from "./page/specialTour/DestinationsPage";
import { ParkGolfPage } from "./page/specialTour/ParkGolfPage";
import { ReviewsPage } from "./page/specialTour/ReviewsPage";
import { ContactPage } from "./page/specialTour/ContactPage";

const siteUrl = "https://www.special-tour.golf";
const defaultImage = `${siteUrl}/special-tour-assets/hokkaido-winter-hero.png`;
const defaultSeo = {
  title: "스페셜투어 | 일본 골프 맞춤 여행",
  description: "스페셜투어는 홋카이도 오비히로 골프, 숙소, 관광 코스를 함께 설계하는 일본 골프 전문 맞춤 투어입니다.",
  keywords: "스페셜투어, 일본 골프투어, 일본 골프여행, 홋카이도 골프, 오비히로 골프, 북해도 관광",
};

const seoByPath: Record<string, { title: string; description: string; keywords: string }> = {
  "/": defaultSeo,
  "/regions": {
    title: "지역 안내 | 스페셜투어 일본 골프 여행",
    description: "홋카이도, 오사카, 큐슈, 오키나와 등 일본 골프 여행 지역별 시즌과 추천 코스를 안내합니다.",
    keywords: "일본 골프 지역, 홋카이도 골프, 오사카 골프, 큐슈 골프, 오키나와 골프",
  },
  "/hotels": {
    title: "오비히로 그랜드 호텔 숙소 안내 | 스페셜투어",
    description: "오비히로 골프 여행에 맞춘 오비히로 그랜드 호텔 객실, 요금, 편의시설, 주변 식당가 정보를 안내합니다.",
    keywords: "오비히로 그랜드 호텔, 오비히로 숙소, 홋카이도 골프 숙소, 일본 골프 호텔",
  },
  "/itineraries": {
    title: "일본 골프 여행 일정 | 스페셜투어",
    description: "라운드, 이동, 숙소, 관광을 함께 묶어 팀 성향에 맞춘 일본 골프 여행 일정을 설계합니다.",
    keywords: "일본 골프 일정, 홋카이도 골프 일정, 맞춤 골프투어, 일본 골프 패키지",
  },
  "/courses": {
    title: "일본 골프장 안내 | 스페셜투어",
    description: "홋카이도 클래식 골프클럽을 포함한 일본 주요 골프장과 라운드 코스를 안내합니다.",
    keywords: "일본 골프장, 홋카이도 클래식 골프클럽, 오비히로 골프장, 일본 라운드",
  },
  "/destinations": {
    title: "북해도 관광지와 일본 로컬 관광 | 스페셜투어",
    description: "오비히로 출발 북해도 관광 코스, 비에이, 아사히다케, 오타루 등 골프 전후 관광지를 안내합니다.",
    keywords: "북해도 관광, 홋카이도 관광 코스, 비에이, 아사히다케, 오타루, 오비히로 관광",
  },
  "/park-golf": {
    title: "북해도 파크골프 여행 | 스페셜투어",
    description:
      "마쿠베츠와 토카치 지역을 중심으로 북해도 파크골프, 온천, 숙소, 관광을 함께 구성하는 맞춤 여행을 안내합니다.",
    keywords: "북해도 파크골프, 홋카이도 파크골프, 마쿠베츠 파크골프, 토카치 파크골프, 일본 파크골프 여행",
  },
  "/reviews": {
    title: "이용 후기와 투어 영상 | 스페셜투어",
    description: "스페셜투어를 다녀온 팀의 이용 후기와 일본 골프투어 영상 안내를 확인하세요.",
    keywords: "스페셜투어 후기, 일본 골프투어 후기, 골프여행 영상, 일본 골프 영상",
  },
  "/contact": {
    title: "견적 문의 | 스페셜투어 일본 골프 맞춤 여행",
    description: "카카오톡, 전화, 이메일로 일본 골프 맞춤 여행 견적과 일정 상담을 문의하세요.",
    keywords: "일본 골프 견적, 골프투어 문의, 스페셜투어 카카오톡, 일본 골프 상담",
  },
};

function upsertMeta(attribute: "name" | "property", value: string, content: string) {
  let element = document.head.querySelector(`meta[${attribute}="${value}"]`) as HTMLMetaElement | null;

  if (!element) {
    element = document.createElement("meta");
    element.setAttribute(attribute, value);
    document.head.appendChild(element);
  }

  element.content = content;
}

function upsertCanonical(href: string) {
  let element = document.head.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;

  if (!element) {
    element = document.createElement("link");
    element.rel = "canonical";
    document.head.appendChild(element);
  }

  element.href = href;
}

function ScrollToTop() {
  const { pathname, hash } = useLocation();

  useEffect(() => {
    if (hash) {
      window.setTimeout(() => {
        document.querySelector(hash)?.scrollIntoView({ behavior: "smooth" });
      }, 0);
      return;
    }

    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [pathname, hash]);

  return null;
}

function SeoManager() {
  const { pathname } = useLocation();

  useEffect(() => {
    const seo = seoByPath[pathname] ?? defaultSeo;
    const url = `${siteUrl}${pathname === "/" ? "/" : pathname}`;

    document.title = seo.title;
    upsertMeta("name", "description", seo.description);
    upsertMeta("name", "keywords", seo.keywords);
    upsertMeta("property", "og:title", seo.title);
    upsertMeta("property", "og:description", seo.description);
    upsertMeta("property", "og:url", url);
    upsertMeta("property", "og:image", defaultImage);
    upsertMeta("name", "twitter:title", seo.title);
    upsertMeta("name", "twitter:description", seo.description);
    upsertMeta("name", "twitter:image", defaultImage);
    upsertCanonical(url);
  }, [pathname]);

  return null;
}

function App() {
  return (
    <BrowserRouter>
      <ScrollToTop />
      <SeoManager />
      <Routes>
        <Route path="/" element={<SpecialTourHomePage />}></Route>
        <Route path="/regions" element={<RegionsPage />}></Route>
        <Route path="/hotels" element={<HotelsPage />}></Route>
        <Route path="/itineraries" element={<ItinerariesPage />}></Route>
        <Route path="/courses" element={<CoursesPage />}></Route>
        <Route path="/destinations" element={<DestinationsPage />}></Route>
        <Route path="/park-golf" element={<ParkGolfPage />}></Route>
        <Route path="/reviews" element={<ReviewsPage />}></Route>
        <Route path="/contact" element={<ContactPage />}></Route>
        <Route path="/golf" element={<HomePageV1 />}></Route>
        <Route path="/used" element={<UsedPage />}></Route>
        <Route path="/used/:code" element={<ClubInfo />}></Route>
        <Route path="/used/survey" element={<SurveyMain />}></Route>
        <Route path="*" element={<NotFoundScene />}></Route>
      </Routes>
    </BrowserRouter>
  );
}

// <div className="mx-auto max-w-[500px] h-screen flex flex-col items-center">
//   <div className="w-screen h-screen flex flex-col items-center">
//   <HomePageV1 />
//   <HomePageV2 />
// </div>

export default App;
